export type ToastTone = 'info' | 'success' | 'warning' | 'error';

export interface ToastMessage {
    id: string;
    message: string;
    tone: ToastTone;
    durationMs: number;
}

export interface ConfirmOptions {
    title?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    tone?: 'default' | 'danger';
}

interface PendingConfirm extends ConfirmOptions {
    id: string;
    resolve: (value: boolean) => void;
}

interface FeedbackSnapshot {
    toasts: ToastMessage[];
    activeConfirm: PendingConfirm | null;
}

class UIFeedbackService {
    private listeners = new Set<() => void>();
    private toasts: ToastMessage[] = [];
    private activeConfirm: PendingConfirm | null = null;
    private confirmQueue: PendingConfirm[] = [];
    private snapshot: FeedbackSnapshot = {
        toasts: this.toasts,
        activeConfirm: this.activeConfirm,
    };

    subscribe = (listener: () => void) => {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    };

    getSnapshot = (): FeedbackSnapshot => this.snapshot;

    notify = (message: string, tone: ToastTone = 'info', durationMs = 3200) => {
        const toast: ToastMessage = {
            id: this.generateId('toast'),
            message,
            tone,
            durationMs,
        };

        this.toasts = [...this.toasts, toast];
        this.emit();
        return toast.id;
    };

    dismissToast = (toastId: string) => {
        this.toasts = this.toasts.filter((toast) => toast.id !== toastId);
        this.emit();
    };

    confirm = (options: ConfirmOptions): Promise<boolean> => {
        return new Promise((resolve) => {
            const request: PendingConfirm = {
                ...options,
                id: this.generateId('confirm'),
                resolve,
                confirmText: options.confirmText ?? 'Confirm',
                cancelText: options.cancelText ?? 'Cancel',
                tone: options.tone ?? 'default',
            };

            if (!this.activeConfirm) {
                this.activeConfirm = request;
                this.emit();
                return;
            }

            this.confirmQueue.push(request);
        });
    };

    resolveConfirm = (value: boolean) => {
        if (!this.activeConfirm) {
            return;
        }

        this.activeConfirm.resolve(value);
        this.activeConfirm = this.confirmQueue.shift() ?? null;
        this.emit();
    };

    resetForTests = () => {
        this.toasts = [];
        this.activeConfirm = null;
        this.confirmQueue = [];
        this.emit();
    };

    private emit() {
        this.snapshot = {
            toasts: this.toasts,
            activeConfirm: this.activeConfirm,
        };
        this.listeners.forEach((listener) => listener());
    }

    private generateId(prefix: string) {
        return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    }
}

export const uiFeedbackService = new UIFeedbackService();

export const notify = {
    info: (message: string, durationMs?: number) => uiFeedbackService.notify(message, 'info', durationMs),
    success: (message: string, durationMs?: number) => uiFeedbackService.notify(message, 'success', durationMs),
    warning: (message: string, durationMs?: number) => uiFeedbackService.notify(message, 'warning', durationMs),
    error: (message: string, durationMs?: number) => uiFeedbackService.notify(message, 'error', durationMs),
};
